import { z } from 'zod';
import { statusToneSchema, uuidSchema } from './common';
import { entityKindSchema } from './entities';
import { linkedEntitySchema } from './links';

/**
 * A date one module wants drawn on another module's timeline
 * (docs/register-collaboration-design.md section 5).
 *
 * The problem this solves: a decision now carries a "decide by" date, and the
 * place people look at dates is the schedule. Three ways of getting it there
 * were rejected before this one --
 *
 *  - **materializing each deadline as a schedule task or milestone**, which is
 *    two records of one fact: the engine would plan around it, dependencies
 *    could point at it, and deleting the risk would strand a task;
 *  - **an `entity_link`**, which records that two items are about the same
 *    thing, and a deadline is not a relation to anything;
 *  - **the schedule page fetching the register directly**, which works for one
 *    source and adds a query, a mapping and a repeated permission check for
 *    every source after it.
 *
 * -- so instead each kind answers for its own dates through `markers?()` on the
 * provider interface it already implements, and the Gantt receives the result
 * as a prop, exactly as it already receives its tasks. Scheduling gains no
 * import and no knowledge of the register; the register gains none of the
 * chart. The only thing that knows about both is the page that shows both.
 */

export const dateMarkerSchema = z.object({
  /** What this is a date *of*. Enough to navigate to it and to group by kind. */
  entityKind: entityKindSchema,
  entityId: uuidSchema,

  /** The calendar day, `YYYY-MM-DD`. A date and not a timestamp: a deadline is
   *  a day in the project's calendar, not an instant in a timezone. */
  on: z.string().date(),

  /**
   * What the date *means*, as a translation key -- `register.field.decideBy`,
   * not "Decide by". The backend does not speak the reader's language
   * (architecture documentation section 16), and this is also how one kind can
   * contribute two differently-named dates later without the chart learning
   * anything.
   */
  meaningKey: z.string(),

  /** The item's name, and the human-authored key beside it (`D-014`). */
  label: z.string(),
  secondary: z.string().nullable(),

  /**
   * How the item is doing, from its own module's rules -- `registerItemTone`
   * for a register item, so the marker, the list row, the agenda card and the
   * dashboard tile cannot disagree about the colour of one thing.
   *
   * Deliberately *not* an "overdue" flag. Whether today is past a calendar date
   * depends on where the reader is standing, and a server-computed answer is
   * wrong for several hours a day for anybody outside the server's timezone.
   * The chart compares `on` to today in the reader's locale.
   */
  statusTone: statusToneSchema,

  /** Where the rest of the application talks about this item, built by the API
   *  so the chart never has to know how another module routes. */
  href: z.string(),
});
export type DateMarker = z.infer<typeof dateMarkerSchema>;

/**
 * How many markers one window may return, per kind.
 *
 * Bounded for the reason section 10.1 bounds everything: a chart three years
 * wide over a project with a busy register would otherwise ship a payload
 * nobody can read anyway. A lane with two hundred pins in it is not a lane, so
 * the ceiling is low and the window is the control.
 */
export const MAX_MARKERS_PER_KIND = 100;

export const markerQuerySchema = z
  .object({
    projectId: uuidSchema,
    /** Inclusive window, as calendar days. Required both ends: an unbounded
     *  marker query is a full table scan wearing a date filter. */
    from: z.string().date(),
    to: z.string().date(),
  })
  .strict()
  .refine((value) => value.from <= value.to, {
    message: 'from must not be after to',
    path: ['from'] as PropertyKey[],
  });
export type MarkerQuery = z.infer<typeof markerQuerySchema>;

export const markerListSchema = z.object({
  markers: z.array(dateMarkerSchema),
  /** True when some kind hit `MAX_MARKERS_PER_KIND`. The chart says so rather
   *  than showing a partial lane as though it were the whole one. */
  truncated: z.boolean(),
});
export type MarkerList = z.infer<typeof markerListSchema>;

/**
 * Resolving a batch of references of one kind to their names, statuses and
 * hrefs -- the one piece of `EntityLinkRegistry.resolve()`'s job that was
 * never reachable from the browser before an inline `[[kind:id]]` reference
 * (docs/rich-text-design.md section 8.3) needed it: every earlier reader of
 * a batch of refs (the recent-comments feed, a meeting's resolved agenda)
 * was itself a server-side caller.
 *
 * One kind per request, matching every other batching in this codebase --
 * `resolveEntityRefs` groups by kind internally for the same reason, and a
 * caller with references of several kinds in one body makes one request per
 * kind rather than this endpoint learning to fan out on their behalf.
 */
export const MAX_RESOLVE_IDS = 50;

export const resolveEntitiesQuerySchema = z
  .object({
    kind: entityKindSchema,
    /** Repeatable: `?id=a&id=b`. */
    id: z
      .union([uuidSchema, z.array(uuidSchema)])
      .transform((value) => (Array.isArray(value) ? value : [value]))
      .pipe(z.array(uuidSchema).min(1).max(MAX_RESOLVE_IDS)),
  })
  .strict();
export type ResolveEntitiesQuery = z.infer<typeof resolveEntitiesQuerySchema>;

/** An id this caller may not read, or that no longer exists, is simply
 *  absent -- the same "resolves to nothing rather than an error" rule
 *  `resolveEntityRefs` already follows, so a dropped reference renders as
 *  unresolved rather than failing the whole batch. */
export const resolvedEntityListSchema = z.object({ items: z.array(linkedEntitySchema) });
export type ResolvedEntityList = z.infer<typeof resolvedEntityListSchema>;
